/* eslint-disable new-cap */
/**
 * REST API を使用したメールテンプレート管理 hooks
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type EmailTemplate = components["schemas"]["EmailTemplate"];
export type EmailTemplateDocuments =
  components["schemas"]["EmailTemplateDocuments"];
export type CreateEmailTemplateInput =
  components["schemas"]["CreateEmailTemplateInput"];
export type EmailTemplateSearchInput =
  components["schemas"]["EmailTemplateSearchInput"];

// 値として使用可能なオブジェクト（既存コードとの互換性）
export const EmailTemplateCategory = {
  Hr: "HR",
  Matter: "MATTER",
  All: "ALL",
} as const;

export type EmailTemplateCategory =
  (typeof EmailTemplateCategory)[keyof typeof EmailTemplateCategory];

// =====================================
// useEmailTemplatesRestApi - メールテンプレート一覧取得
// =====================================

export interface UseEmailTemplatesRestApiOptions {
  filter?: EmailTemplateSearchInput;
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseEmailTemplatesRestApiResult {
  data: { emailTemplates: EmailTemplateDocuments } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<EmailTemplateDocuments>;
}

/**
 * メールテンプレート一覧を取得するhook
 * @param {UseEmailTemplatesRestApiOptions} options - オプション設定
 * @return {UseEmailTemplatesRestApiResult} テンプレート一覧、エラー、ローディング状態、refetch関数
 */
export function useEmailTemplatesRestApi(
  options: UseEmailTemplatesRestApiOptions = {},
): UseEmailTemplatesRestApiResult {
  const [data, setData] = useState<
    { emailTemplates: EmailTemplateDocuments } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchEmailTemplates =
    useCallback(async (): Promise<EmailTemplateDocuments> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {};
        if (options.filter) {
          params.filter = JSON.stringify(options.filter);
        }

        const { data: responseData, error: apiError } = await api.GET(
          "/api/emails/templates",
          {
            params: {
              query: Object.keys(params).length > 0 ? params : undefined,
            },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        setData({ emailTemplates: responseData });
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    }, [options.filter, options.onCompleted]);

  const initialFetchDone = useRef(false);
  const skipRef = useRef(options.skip);

  useEffect(() => {
    if (!skipRef.current && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchEmailTemplates().catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchEmailTemplates]);

  return {
    data,
    error,
    loading,
    refetch: fetchEmailTemplates,
  };
}

// =====================================
// useCreateEmailTemplateRestApi - メールテンプレート作成
// =====================================

export interface UseCreateEmailTemplateRestApiResult {
  createEmailTemplate: (
    input: CreateEmailTemplateInput,
  ) => Promise<{ success: boolean; message: string }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * メールテンプレートを作成するhook
 * @return {UseCreateEmailTemplateRestApiResult} 作成関数、ローディング状態、エラー
 */
export function useCreateEmailTemplateRestApi(): UseCreateEmailTemplateRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createEmailTemplate = useCallback(
    async (
      input: CreateEmailTemplateInput,
    ): Promise<{ success: boolean; message: string }> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.POST(
          "/api/emails/templates",
          {
            body: input,
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createEmailTemplate,
    loading,
    error,
  };
}

export default {
  useEmailTemplatesRestApi,
  useCreateEmailTemplateRestApi,
};
